import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _extends from "@babel/runtime/helpers/esm/extends";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
var _excluded = ["children", "input", "placeholder", "inputProps", "anchorDirection", "renderComponent", "className", "style", "jss", "onChange", "renderSelected", "value", "variant", "single", "plural", "searchable", "onSearchQueryChange", "withSelectAll", "disabled", "emptyStateComponent", "name", "error", "menuWidth", "infiniteScrollOptions"];
import React, { useRef, useState, useEffect, useCallback } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import isFunction from 'lodash/isFunction';
import createStyles from "../createStyles";
import Menu from "../Menu/Menu";
import composeEvent from "../utils/composeEvent";
import TextField from "../TextField";
import InfiniteScroll from "../InfiniteScroll";
import DropdownInput from "./DropdownInput";
import DropdownSearch from "./DropdownSearch";
import DropdownMenuList from "./DropdownMenuList";
import SelectAllOption from "./SelectAllOption";
import { AnchorDirectionType } from "../enums";
export var useStyles = createStyles(function (theme) {
  return {
    root: {
      display: 'flex',
      width: '100%',
      position: 'relative',
      flexFlow: 'column'
    },
    input: {
      marginLeft: theme.spacer(1),
      width: "calc(100% - ".concat(theme.spacer(2), "px)")
    },
    menu: {
      overflowY: 'hidden',
      maxHeight: '100%'
    },
    menuList: _defineProperty({
      maxHeight: 'min(calc(50vh - 44px), 260px)',
      padding: 0
    }, theme.breakpoints.create('sm'), {
      maxHeight: 260
    }),
    scrollable: {
      overflowY: 'auto'
    },
    searchableMaxHeight: _defineProperty({
      maxHeight: 'min(calc(50vh - 84px), 260px)',
      padding: 0
    }, theme.breakpoints.create('sm'), {
      maxHeight: 220
    }),
    initialLoading: {
      height: 180
    },
    emptyState: {
      color: theme.palette.text.secondary,
      paddingLeft: theme.spacer(1),
      paddingTop: theme.spacer(1)
    }
  };
}, {
  name: 'Dropdown'
});
var loaderOverride = {
  loader: {
    height: '100%'
  }
};

var defaultClearSearchInputEvent = _extends({}, new Event('SearchInputQuery'), {
  target: {
    value: ''
  }
});

var Dropdown = /*#__PURE__*/React.forwardRef(function Dropdown(_ref, ref) {
  var _cn;

  var children = _ref.children,
      input = _ref.input,
      placeholder = _ref.placeholder,
      inputProps = _ref.inputProps,
      anchorDirection = _ref.anchorDirection,
      renderComponent = _ref.renderComponent,
      className = _ref.className,
      style = _ref.style,
      jss = _ref.jss,
      onChange = _ref.onChange,
      renderSelected = _ref.renderSelected,
      value = _ref.value,
      variant = _ref.variant,
      single = _ref.single,
      plural = _ref.plural,
      searchable = _ref.searchable,
      onSearchQueryChange = _ref.onSearchQueryChange,
      withSelectAll = _ref.withSelectAll,
      disabled = _ref.disabled,
      emptyStateComponent = _ref.emptyStateComponent,
      name = _ref.name,
      error = _ref.error,
      menuWidth = _ref.menuWidth,
      infiniteScrollOptions = _ref.infiniteScrollOptions,
      props = _objectWithoutProperties(_ref, _excluded);

  var classes = useStyles({
    jss: jss
  });
  var dropdownAnchorRef = useRef();
  var searchInputRef = useRef();
  var menuListRef = useRef();
  var wasOpen = useRef(false);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      open = _useState2[0],
      setOpen = _useState2[1];

  var _useState3 = useState(''),
      _useState4 = _slicedToArray(_useState3, 2),
      query = _useState4[0],
      setQuery = _useState4[1];

  var isSingleSelect = variant === 'single';
  var clearSearchInputQuery = useCallback(function () {
    var e = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : defaultClearSearchInputEvent;
    setQuery('');

    if (isFunction(onSearchQueryChange)) {
      onSearchQueryChange(e);
    }
  }, [onSearchQueryChange]);
  useEffect(function () {
    var _searchInputRef$curre;

    if (open) {
      wasOpen.current = true;
    }

    if (open && searchable && isFunction(searchInputRef === null || searchInputRef === void 0 ? void 0 : (_searchInputRef$curre = searchInputRef.current) === null || _searchInputRef$curre === void 0 ? void 0 : _searchInputRef$curre.focus)) {
      window.requestAnimationFrame(function () {
        return searchInputRef.current.focus();
      });
    }

    if (!open && wasOpen.current && query && onSearchQueryChange) {
      clearSearchInputQuery();
    }
  }, [open]);

  var handleInputClick = function handleInputClick() {
    setOpen(!open);
  };

  var handleDismissDropdown = function handleDismissDropdown() {
    setOpen(false);
  };

  var dispatchOnChange = function dispatchOnChange(e, selectedValue) {
    e.stopPropagation();
    var newEvent = composeEvent(e, {
      name: name,
      value: selectedValue
    });
    onChange(newEvent, selectedValue);

    if (isSingleSelect) {
      handleDismissDropdown();
    }
  };

  var handleSelectItem = useCallback(function (e, menuItemValue) {
    var _searchInputRef$curre2;

    clearSearchInputQuery();
    dispatchOnChange(e, menuItemValue);

    if (isFunction(searchInputRef === null || searchInputRef === void 0 ? void 0 : (_searchInputRef$curre2 = searchInputRef.current) === null || _searchInputRef$curre2 === void 0 ? void 0 : _searchInputRef$curre2.focus)) {
      window.requestAnimationFrame(function () {
        return searchInputRef.current.focus();
      });
    }
  }, [clearSearchInputQuery, dispatchOnChange]);

  var handleSelectAllToggle = function handleSelectAllToggle(e, values) {
    menuListRef.current.reset();
    dispatchOnChange(e, values);
  };

  var handleSearchInputChange = function handleSearchInputChange(e) {
    var searchQueryValue = e.currentTarget.value;
    setQuery(searchQueryValue);
    onSearchQueryChange(composeEvent(e, {
      name: name
    }));
  };

  var handleClearSearchInputQuery = function handleClearSearchInputQuery(e) {
    clearSearchInputQuery(composeEvent(e, {
      name: name,
      value: ''
    }));
  };

  var handleClearValue = function handleClearValue(e) {
    // Due to bug in redux form, we must pass null
    // see details here: https://github.com/redux-form/redux-form/issues/3946
    dispatchOnChange(e, null);
  };

  var isLoading = infiniteScrollOptions.isLoading;
  var InputComponent = input || /*#__PURE__*/React.createElement(DropdownInput, null);
  var shouldShowEmptyState = isEmpty(children) && !isLoading;
  var isInfiniteScroll = infiniteScrollOptions.totalCount;
  var emptyStateMessage = searchable && query ? 'No results found.' : emptyStateComponent;
  return /*#__PURE__*/React.createElement("div", _extends({
    className: cn(classes.root, className),
    style: style,
    ref: ref
  }, props), /*#__PURE__*/React.cloneElement(InputComponent, _extends({
    ref: dropdownAnchorRef,
    open: open,
    name: name,
    variant: variant,
    disabled: disabled,
    value: value,
    single: single,
    plural: plural,
    placeholder: placeholder,
    renderComponent: renderComponent,
    renderSelected: renderSelected,
    onClear: handleClearValue,
    onClick: handleInputClick,
    onChange: dispatchOnChange,
    error: error
  }, inputProps)), /*#__PURE__*/React.createElement(Menu, {
    variant: "menu",
    menuWidth: menuWidth,
    open: open,
    onClose: handleDismissDropdown,
    className: classes.menu,
    anchorDirection: anchorDirection,
    originDirection: "bottom",
    fitAnchor: true,
    anchor: dropdownAnchorRef
  }, searchable && /*#__PURE__*/React.createElement(DropdownSearch, {
    ref: searchInputRef,
    value: query,
    onChange: handleSearchInputChange,
    onClear: handleClearSearchInputQuery,
    className: classes.input
  }), withSelectAll && !isSingleSelect && /*#__PURE__*/React.createElement(SelectAllOption, {
    options: children,
    selectedOptions: value,
    onChange: handleSelectAllToggle
  }), /*#__PURE__*/React.createElement(Menu, {
    variant: "list",
    elevation: 0,
    className: cn(classes.menuList, (_cn = {}, _defineProperty(_cn, classes.initialLoading, isLoading && isEmpty(children)), _defineProperty(_cn, classes.scrollable, !isInfiniteScroll), _defineProperty(_cn, classes.searchableMaxHeight, searchable), _cn))
  }, /*#__PURE__*/React.createElement(InfiniteScroll, _extends({}, infiniteScrollOptions, {
    jss: loaderOverride
  }), /*#__PURE__*/React.createElement(DropdownMenuList, {
    ref: menuListRef,
    variant: variant,
    value: value,
    disabled: disabled,
    onChange: handleSelectItem
  }, children))), shouldShowEmptyState && emptyStateMessage && /*#__PURE__*/React.createElement(TextField, {
    className: cn(classes.emptyState)
  }, emptyStateMessage)));
});
Dropdown.defaultProps = {
  searchable: false,
  placeholder: 'Select an option',
  onSearchQueryChange: null,
  className: '',
  style: {},
  name: '',
  value: undefined,
  single: 'more',
  plural: 'more',
  disabled: false,
  variant: 'single',
  emptyStateComponent: null,
  renderComponent: null,
  infiniteScrollOptions: {},
  anchorDirection: 'start'
};
Dropdown.propTypes = {
  /** Dropdown menuItem options to provide to the dropdown
   * Use <MenuItem> component for rendering children
   */
  children: PropTypes.arrayOf(PropTypes.node).isRequired,

  /** Indicator whether or not the input field is searchable */
  searchable: PropTypes.bool,

  /** Placeholder for input field */
  placeholder: PropTypes.string,

  /** Empty state component  to display when there're no results */
  emptyStateComponent: PropTypes.node,

  /** Mandatory if searchable - callback for on input change */
  onSearchQueryChange: PropTypes.func,

  /** Callback function to be called on value changed - arguments - (event, value)
   * event will contain the html dom value converted to string, to access the full value, use the second argument
   * */
  onChange: PropTypes.func.isRequired,

  /** Additional class for dropdown root */
  className: PropTypes.string,

  /** Additional style for dropdown root */
  style: PropTypes.shape(),

  /** form element name */
  name: PropTypes.string,

  /** Selected dropdown value */
  value: PropTypes.oneOfType([PropTypes.shape(), PropTypes.array, PropTypes.string, PropTypes.number]),

  /** String, to display placeholder with 2 values */
  single: PropTypes.string,

  /** String, to display placeholder with more than 2 values */
  plural: PropTypes.string,

  /** Set the dropdown content menu width manually - by default it will resolve to the larger of the input width or the content width */
  menuWidth: PropTypes.number,

  /** Indicator for disabled status */
  disabled: PropTypes.bool,

  /** variant for the type of dropdown we want to render - values [single, multiselect, chip] */
  variant: PropTypes.oneOf(['single', 'multiselect', 'chip']),

  /** custom props to pass to the input component */
  inputProps: PropTypes.shape(),

  /** DEPRECATED- USE renderSelected */
  renderComponent: PropTypes.func,

  /** Set the input component to show on selected item, Accepting function and String
   * If strings - will render the value[renderSelected].
   * If function - will pass the value props
   * */
  renderSelected: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),

  /** Object to define infinite scrolling behaviour */
  infiniteScrollOptions: PropTypes.shape({
    /** starting index of the chunk */
    skip: PropTypes.number,

    /** cb to notify about required loads */
    onLoadMore: PropTypes.func,

    /** total number of entries */
    totalCount: PropTypes.number,

    /** offset from the bottom, from which loading the next chunk should be triggered */
    scrollOffset: PropTypes.number,

    /** number of rows per page */
    pageSize: PropTypes.number,

    /** indicator for data fetching state */
    isLoading: PropTypes.bool
  }),

  /** a button that selects/unselects all options */
  withSelectAll: PropTypes.bool,

  /** Set the direction of the dropdown anchor */
  anchorDirection: PropTypes.oneOf([AnchorDirectionType.Center, AnchorDirectionType.Start, AnchorDirectionType.End])
};
Dropdown.displayName = 'Dropdown';
export default Dropdown;